import { useEffect, useState } from 'react'

import { formatDate } from 'commons/utils/date'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import {
  type HeadItemType,
  Icon,
  Input,
  SectionCard,
  SlimButton,
  Table,
  Tooltip,
  Typography,
} from 'components'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { useParams } from 'react-router-dom'
import {
  checkOVBindStatus,
  getOvBinds,
  ovBindOperation,
  resetBindValidationStatus,
} from 'store/OVContext/ovSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

export const OVBindSection = () => {
  const dispatch = useDispatch()
  const { ovNumber } = useParams()
  const [ovNumberInput, setOvNumberInput] = useState('')
  const [unbindModal, setUnbindModal] = useState(false)
  const [ovToUnbind, setOvToUnbind] = useState('')
  const {
    loadingBind,
    validationSuccess,
    validationError,
    bindOvsList,
    bindTotalizers,
    ovSelected: {
      Cabecalho: { CodigoStatus },
    },
  } = useSelector((state) => state.ov)

  const closeUnbindModal = () => setUnbindModal(false)

  const bindInputOV = () => {
    dispatch(
      ovBindOperation({
        numeroordemvenda: ovNumber ?? '',
        numeroordemvendarefe: ovNumberInput,
        tipoOperacao: 'I',
      }),
    )
    setOvNumberInput('')
  }

  const unbindOV = (selectedOV: string) => {
    dispatch(
      ovBindOperation({
        numeroordemvenda: ovNumber ?? '',
        numeroordemvendarefe: selectedOV,
        tipoOperacao: 'E',
      }),
    )
  }

  const tableHead: HeadItemType[] = [
    { columnName: 'DataOV', label: 'Data da OV' },
    { columnName: 'MesCota', label: 'Mês cota' },
    { columnName: 'OVPortal', label: 'OV Portal' },
    { columnName: 'OVSAP', label: 'OV SAP' },
    { columnName: 'Fabrica', label: 'Fábrica' },
    { columnName: 'Qtd', label: 'Qtd.' },
    { columnName: 'Valor', label: 'Valor (R$)' },
    { columnName: 'PrevFaturamento', label: 'Prev. Faturamento' },
    { columnName: 'PrevEntrega', label: 'Prev. Entrega' },
    { columnName: 'Acoes', label: 'Ações' },
  ]

  const tableData = () => {
    return bindOvsList.map((ov) => {
      return {
        content: [
          ov.DataDigitacao,
          formatDate(ov.MesCota, 'MM/YYYY', 'YYYYMM'),
          ov.NumeroOrdemVenda,
          ov.NumeroOrdemVendaSap,
          ov.CodigoCentroMaterial,
          numberToDecimalString(ov.QuantidadeTotalItem),
          formatToPTBRNumber(ov.ValorTotalItem),
          ov.DataPrevistaFaturamento,
          ov.DataPrevistaEntrega,
          <S.ActionButtonWrapper key={`unbind-${ov.NumeroOrdemVenda}`}>
            <Tooltip arrow placement="top" title={'Desvincular OV'}>
              <S.ActionButton
                disabled={loadingBind || +CodigoStatus !== 0}
                id={`unbind-${ov.NumeroOrdemVenda}-button`}
                onClick={() => {
                  setOvToUnbind(ov.NumeroOrdemVenda)
                  setUnbindModal(true)
                }}
              >
                <Icon color={theme.colors.neutral.white} icon="cancel" size="15" />
              </S.ActionButton>
            </Tooltip>
          </S.ActionButtonWrapper>,
        ],
        id: ov.NumeroOrdemVenda,
      }
    })
  }

  useEffect(() => {
    dispatch(getOvBinds(ovNumber ?? ''))
  }, [])

  useEffect(() => {
    if (ovNumberInput.length === 15) {
      dispatch(
        checkOVBindStatus({
          numeroOrdemVenda: ovNumber ?? '',
          numeroOrdemVendaRefe: ovNumberInput,
        }),
      )
    } else {
      dispatch(resetBindValidationStatus())
    }
  }, [ovNumberInput])

  return (
    <S.CardWrapper>
      <SectionCard
        title="Vincular OVs YPEM"
        tooltip
        tooltipText="Esta é uma ação necessária e obrigatória para o registro da OV, pois não é possível enviar uma OV YRBA sem vinculá-la a nenhuma OV YPEM."
      >
        <S.ContentWrapper>
          <Typography design="hav-default-03-text">
            Adicione as OVs necessárias para gerar a YRBA:
          </Typography>
          <S.InsertRow>
            <S.InputWrapper>
              <Input
                disabled={loadingBind || +CodigoStatus !== 0}
                error={validationError}
                helperText={validationSuccess}
                id="ov-bind-input"
                label="Nº da OV Portal"
                maxLength={15}
                onChange={(e) => setOvNumberInput(e.target.value)}
                placeholder="Digite aqui"
                required
                successOutline={!!validationSuccess}
                value={ovNumberInput}
                width={180}
              />
            </S.InputWrapper>
            <S.ButtonWrapper>
              <SlimButton
                content="Adicionar"
                disabled={!validationSuccess || +CodigoStatus !== 0}
                height={30}
                id="bind-ov-button"
                loading={loadingBind}
                onClick={bindInputOV}
                variant="primary"
              />
            </S.ButtonWrapper>
          </S.InsertRow>
          {!!bindOvsList.length && (
            <>
              <S.TableWrapper>
                <Table
                  bodyData={tableData()}
                  customAlign={[{ colIndex: 7, textAlign: 'center' }]}
                  headData={tableHead}
                  rowHeight={40}
                />
              </S.TableWrapper>
              <S.TotalizerWrapper>
                <S.TotalizerContainer>
                  <S.TotalizerItem>
                    <Typography design="hav-h7-text">Qtd. total</Typography>
                    <span>{numberToDecimalString(bindTotalizers.QuantidadeTotal)}</span>
                  </S.TotalizerItem>
                  <S.TotalizerItem>
                    <Typography design="hav-h7-text">Valor total</Typography>
                    <span>{formatToPTBRNumber(bindTotalizers.ValorTotal)}</span>
                  </S.TotalizerItem>
                </S.TotalizerContainer>
              </S.TotalizerWrapper>
            </>
          )}
        </S.ContentWrapper>
      </SectionCard>
      {unbindModal && (
        <ModalContentLayout
          closeAction={closeUnbindModal}
          closeButton
          description="Essa ação irá desvincular essa OV YPEM do seu pedido."
          title="Desvincular OV"
          titleSize={22}
          width={480}
        >
          <S.ModalActions>
            <SlimButton content="Voltar" onClick={closeUnbindModal} variant="secondary" />
            <SlimButton
              content="Desvincular"
              onClick={() => {
                unbindOV(ovToUnbind)
                closeUnbindModal()
                setOvToUnbind('')
              }}
              variant="danger"
            />
          </S.ModalActions>
        </ModalContentLayout>
      )}
    </S.CardWrapper>
  )
}
